import React from "react";
import { Handle, NodeProps, Position } from "@xyflow/react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useFeature } from "@/hooks/useFeature";
import { useFlowBuilder } from "../components/FlowBuilderContext";
import { ActionTypeOption, PICKER_ROWS } from "./actionTypeCatalog";
import { CallFlowActionType } from "@/model/interface/CallFlowActionType";

interface StepButtonProps {
  candidate?: ActionTypeOption;
  isLocked: boolean;
  onChoose: (actionType: CallFlowActionType) => void;
}

function StepButton({ candidate, isLocked, onChoose }: StepButtonProps) {
  if (!candidate) {
    return <div />;
  }
  const CandidateIcon = candidate.icon;
  return (
    <button
      type="button"
      disabled={isLocked}
      title={isLocked ? "Available on Pro" : undefined}
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm font-medium",
        isLocked ? "cursor-not-allowed opacity-50" : "hover:border-primary hover:bg-primary/5",
      )}
      onClick={() => onChoose(candidate.actionType)}
    >
      <CandidateIcon className="h-4 w-4 shrink-0 text-primary" />
      <span className="leading-tight">{candidate.label}</span>
    </button>
  );
}

export default function PickerNode({ id, selected }: NodeProps) {
  const { orientation, chooseActionType, deleteNode } = useFlowBuilder();
  const hasRoutingSteps = useFeature("CALL_ROUTING_STEPS");
  const navigate = useNavigate();
  const isHorizontal = orientation === "horizontal";

  const choose = (actionType: CallFlowActionType) => {
    chooseActionType(id, actionType);
  };

  return (
    <Card className={cn("nopan relative w-[480px] border-dashed shadow-none", selected && "border-primary")}>
      {!isHorizontal && <Handle type="target" position={Position.Top} />}

      <div className="relative flex items-center justify-between gap-2 border-b px-4 py-3">
        {isHorizontal && <Handle type="target" position={Position.Left} />}
        <div className="min-w-0">
          <div className="text-base font-semibold">New step</div>
          <div className="text-sm text-muted-foreground">Pick what happens here</div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          title="Remove this step"
          onClick={() => deleteNode(id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 p-4">
        {!hasRoutingSteps && (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Standard</div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pro</span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => navigate("/manage-organisation/plans")}
              >
                Upgrade to Pro
              </Button>
            </div>
          </>
        )}
        {PICKER_ROWS.map((row) => (
          <React.Fragment key={row.standard?.actionType ?? row.pro?.actionType}>
            <StepButton candidate={row.standard} isLocked={false} onChoose={choose} />
            <StepButton candidate={row.pro} isLocked={!hasRoutingSteps} onChoose={choose} />
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
}
